import { crossActionTypes, loanActionTypes } from '@0xtorch/core'
import { sql } from 'drizzle-orm'
import { z } from 'zod'
import type { DatabaseWithTransaction } from '../../types'

/** 大量の警告 ID を画面とメモリへ展開しないための分類別表示上限。 */
export const warningIdPreviewLimit = 100
/** SQLite で集約した ID 配列を検証する。 */
const idsSchema = z.array(z.string()).max(warningIdPreviewLimit)

/**
 * 警告グループを一度だけ集計し、件数と上限付きの ID 一覧を取得する。
 * 期間外の相手取引も判定に含め、対象期間の件数だけを加算する。
 * @param params DB と対象期間。
 * @returns 重複しない警告件数、分類別の ID 抜粋、抜粋上限の超過有無。
 */
export const selectWarningSummary = async (params: {
  database: DatabaseWithTransaction
  from: number
  to: number
}) => {
  const {
    database: { database },
    from,
    to,
  } = params
  const start = Math.min(from, to)
  const end = Math.max(from, to)
  const crossTypes = sql.join(
    [...crossActionTypes].map((type) => sql`${type}`),
    sql`, `,
  )
  const loanTypes = sql.join(
    [...loanActionTypes].map((type) => sql`${type}`),
    sql`, `,
  )
  const result = sql`(
    WITH crosses AS MATERIALIZED (
      SELECT crossId id, count(*) total,
        sum(CASE WHEN crossType = 'start' THEN 1 ELSE 0 END) starts,
        sum(CASE WHEN crossType = 'end' THEN 1 ELSE 0 END) ends,
        sum(timestamp BETWEEN ${start} AND ${end}) periodCount
      FROM action WHERE type IN (${crossTypes}) AND crossId IS NOT NULL AND crossId <> '' GROUP BY crossId
    ), loans AS MATERIALIZED (
      SELECT loanId id, count(*) total, sum(timestamp BETWEEN ${start} AND ${end}) periodCount
      FROM action WHERE type IN (${loanTypes}) AND loanId IS NOT NULL AND loanId <> '' GROUP BY loanId
    )
    SELECT
      (SELECT coalesce(sum(periodCount), 0) FROM crosses WHERE periodCount > 0 AND (total >= 3 OR starts >= 2 OR ends >= 2 OR starts = 0 OR ends = 0)) +
      (SELECT coalesce(sum(periodCount), 0) FROM loans WHERE periodCount > 0 AND (total >= 3 OR total = 1)) totalCount,
      (SELECT json_group_array(id) FROM (SELECT id FROM crosses WHERE periodCount > 0 AND total >= 3 ORDER BY id LIMIT ${warningIdPreviewLimit})) greaterCross,
      (SELECT json_group_array(id) FROM (SELECT id FROM loans WHERE periodCount > 0 AND total >= 3 ORDER BY id LIMIT ${warningIdPreviewLimit})) greaterLoan,
      (SELECT json_group_array(id) FROM (SELECT id FROM crosses WHERE periodCount > 0 AND (starts >= 2 OR ends >= 2) ORDER BY id LIMIT ${warningIdPreviewLimit})) duplicateCross,
      (SELECT json_group_array(id) FROM (SELECT id FROM crosses WHERE periodCount > 0 AND (starts = 0 OR ends = 0) ORDER BY id LIMIT ${warningIdPreviewLimit})) openCross,
      (SELECT json_group_array(id) FROM (SELECT id FROM loans WHERE periodCount > 0 AND total = 1 ORDER BY id LIMIT ${warningIdPreviewLimit})) singleLoan,
      (SELECT count(*) FROM crosses WHERE periodCount > 0 AND total >= 3) > ${warningIdPreviewLimit} OR
      (SELECT count(*) FROM loans WHERE periodCount > 0 AND total >= 3) > ${warningIdPreviewLimit} OR
      (SELECT count(*) FROM crosses WHERE periodCount > 0 AND (starts >= 2 OR ends >= 2)) > ${warningIdPreviewLimit} OR
      (SELECT count(*) FROM crosses WHERE periodCount > 0 AND (starts = 0 OR ends = 0)) > ${warningIdPreviewLimit} OR
      (SELECT count(*) FROM loans WHERE periodCount > 0 AND total = 1) > ${warningIdPreviewLimit} truncated
  )`
  const [row] = await database
    .select({
      count: sql<number>`totalCount`.mapWith(Number),
      greaterCross: sql<string>`greaterCross`,
      greaterLoan: sql<string>`greaterLoan`,
      duplicateCross: sql<string>`duplicateCross`,
      openCross: sql<string>`openCross`,
      singleLoan: sql<string>`singleLoan`,
      isTruncated: sql<boolean>`truncated`.mapWith(Boolean),
    })
    .from(result)
  if (row === undefined) throw new Error('Warning summary not found')
  return {
    count: row.count,
    warningIds: {
      'greater-than-two-cross': idsSchema.parse(JSON.parse(row.greaterCross)),
      'greater-than-two-loan': idsSchema.parse(JSON.parse(row.greaterLoan)),
      'multiple-start-end-cross': idsSchema.parse(
        JSON.parse(row.duplicateCross),
      ),
      'not-close-cross': idsSchema.parse(JSON.parse(row.openCross)),
      'only-one-loan': idsSchema.parse(JSON.parse(row.singleLoan)),
    },
    isTruncated: row.isTruncated,
  }
}
